import { useState, useMemo } from 'react';
import { UserAccessRow, ActivityStatus } from '../../../../hooks/useAccessLogs';

interface Props {
  userTable: UserAccessRow[];
  plans: { id: string; name: string }[];
}

// ─── Meta maps ─────────────────────────────────────────────────────────────

const ACTIVITY_META: Record<ActivityStatus, { label: string; cls: string; icon: string }> = {
  active:       { label: 'Ativo (7d)',       cls: 'bg-emerald-500/20 text-emerald-400', icon: 'ri-checkbox-circle-line'  },
  inactive_7d:  { label: 'Inativo (8–15d)',  cls: 'bg-amber-500/20 text-amber-400',    icon: 'ri-time-line'             },
  inactive_15d: { label: 'Inativo (16–30d)', cls: 'bg-orange-500/20 text-orange-400',  icon: 'ri-alarm-warning-line'    },
  inactive_30d: { label: 'Sem acesso (30d+)',cls: 'bg-red-500/20 text-red-400',        icon: 'ri-error-warning-line'    },
  never:        { label: 'Nunca acessou',    cls: 'bg-white/10 text-white/30',          icon: 'ri-forbid-line'           },
};

const DEVICE_ICONS: Record<string, string> = {
  desktop: 'ri-computer-line',
  mobile:  'ri-smartphone-line',
  tablet:  'ri-tablet-line',
};

type SortCol = 'last_access' | 'sessions' | 'name';

const PAGE_SIZE = 20;

const fmtDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('pt-BR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  } as Intl.DateTimeFormatOptions);
};

// ─── Component ─────────────────────────────────────────────────────────────

export const AccessTable = ({ userTable, plans }: Props) => {
  const [search, setSearch]           = useState('');
  const [planFilter, setPlanFilter]   = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [deviceFilter, setDeviceFilter] = useState('');
  const [page, setPage]               = useState(1);
  const [sortBy, setSortBy]           = useState<SortCol>('last_access');
  const [sortDir, setSortDir]         = useState<'asc' | 'desc'>('desc');

  const handleSort = (col: SortCol) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('desc'); }
  };

  const filtered = useMemo(() => {
    let r = [...userTable];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter((u) => u.full_name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q));
    }
    if (planFilter) r = r.filter((u) => u.plan_id === planFilter);
    if (statusFilter) r = r.filter((u) => u.activity_status === statusFilter);
    if (deviceFilter) r = r.filter((u) => (u.device_type ?? '') === deviceFilter);

    r.sort((a, b) => {
      let va: string | number;
      let vb: string | number;
      if (sortBy === 'last_access') { va = a.last_access ?? ''; vb = b.last_access ?? ''; }
      else if (sortBy === 'sessions') { va = a.sessions_in_period; vb = b.sessions_in_period; }
      else { va = a.full_name.toLowerCase(); vb = b.full_name.toLowerCase(); }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return r;
  }, [userTable, search, planFilter, statusFilter, deviceFilter, sortBy, sortDir]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const resetPage = () => setPage(1);

  const SortIcon = ({ col }: { col: SortCol }) => (
    <i className={`ml-1 text-[10px] ${sortBy === col ? 'ri-arrow-down-s-line text-white/60' : 'ri-arrow-up-s-line text-white/20'} ${sortBy === col && sortDir === 'asc' ? 'ri-arrow-up-s-line' : ''}`} />
  );

  // Inactivity summary stats
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    filtered.forEach((u) => { counts[u.activity_status] = (counts[u.activity_status] || 0) + 1; });
    return counts;
  }, [filtered]);

  return (
    <div className="space-y-4">
      {/* LGPD notice */}
      <div className="flex items-start gap-2.5 px-4 py-3 bg-violet-500/10 border border-violet-500/20 rounded-xl">
        <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
          <i className="ri-shield-check-line text-violet-400 text-[14px]" />
        </div>
        <p className="text-[12px] text-violet-300 leading-relaxed">
          <strong>LGPD — Acesso restrito a administradores.</strong>{' '}
          Esta tabela contém dados de sessão para fins administrativos e de segurança interna. Não divulgar externamente.
        </p>
      </div>

      {/* Summary badges */}
      <div className="flex flex-wrap gap-2">
        {(Object.entries(ACTIVITY_META) as [ActivityStatus, typeof ACTIVITY_META[ActivityStatus]][]).map(([key, meta]) => {
          const count = statusCounts[key] || 0;
          if (count === 0) return null;
          return (
            <button
              key={key}
              onClick={() => { setStatusFilter(statusFilter === key ? '' : key); resetPage(); }}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-600 cursor-pointer transition-all border ${
                statusFilter === key
                  ? meta.cls + ' border-current'
                  : 'bg-white/5 border-white/10 text-white/40 hover:text-white/70'
              }`}
            >
              <i className={`${meta.icon} text-[11px]`} />
              {meta.label}
              <span className="ml-0.5 opacity-70">{count}</span>
            </button>
          );
        })}
        {statusFilter && (
          <button
            onClick={() => { setStatusFilter(''); resetPage(); }}
            className="px-2.5 py-1 rounded-lg text-[11px] text-white/40 hover:text-white/70 bg-white/5 border border-white/10 cursor-pointer transition-colors"
          >
            <i className="ri-close-line mr-1" />Limpar filtro
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 flex-1 min-w-[180px] max-w-xs">
          <i className="ri-search-line text-white/30 text-[14px]" />
          <input
            className="bg-transparent text-white text-[13px] outline-none placeholder:text-white/30 w-full"
            placeholder="Nome ou e-mail..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); resetPage(); }}
          />
          {search && (
            <button onClick={() => { setSearch(''); resetPage(); }} className="text-white/30 hover:text-white/60 cursor-pointer">
              <i className="ri-close-line text-[14px]" />
            </button>
          )}
        </div>

        <select
          value={planFilter}
          onChange={(e) => { setPlanFilter(e.target.value); resetPage(); }}
          className="bg-white/5 border border-white/10 text-white text-[13px] rounded-xl px-3 py-2 cursor-pointer outline-none"
        >
          <option value="" className="bg-[#1a1a2e]">Todos os planos</option>
          {plans.map((p) => (
            <option key={p.id} value={p.id} className="bg-[#1a1a2e]">{p.name}</option>
          ))}
        </select>

        <select
          value={deviceFilter}
          onChange={(e) => { setDeviceFilter(e.target.value); resetPage(); }}
          className="bg-white/5 border border-white/10 text-white text-[13px] rounded-xl px-3 py-2 cursor-pointer outline-none"
        >
          <option value="" className="bg-[#1a1a2e]">Todos os dispositivos</option>
          <option value="desktop" className="bg-[#1a1a2e]">Desktop</option>
          <option value="mobile" className="bg-[#1a1a2e]">Mobile</option>
          <option value="tablet" className="bg-[#1a1a2e]">Tablet</option>
        </select>

        <span className="text-[12px] text-white/30 ml-auto whitespace-nowrap">
          {filtered.length} usuário{filtered.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="border-b border-white/10">
                <th
                  onClick={() => handleSort('name')}
                  className="text-left px-5 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider cursor-pointer select-none hover:text-white/60 transition-colors"
                >
                  Usuário <SortIcon col="name" />
                </th>
                <th className="text-left px-4 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider">Plano</th>
                <th
                  onClick={() => handleSort('last_access')}
                  className="text-left px-4 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider cursor-pointer select-none hover:text-white/60 transition-colors"
                >
                  Último acesso <SortIcon col="last_access" />
                </th>
                <th
                  onClick={() => handleSort('sessions')}
                  className="text-left px-4 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider cursor-pointer select-none hover:text-white/60 transition-colors"
                >
                  Sessões <SortIcon col="sessions" />
                </th>
                <th className="text-left px-4 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider">Dispositivo</th>
                <th className="text-left px-4 py-3 text-[11px] font-600 text-white/40 uppercase tracking-wider">Status de Acesso</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map((u) => {
                const activity = ACTIVITY_META[u.activity_status];
                const deviceIcon = DEVICE_ICONS[u.device_type ?? ''] || 'ri-question-line';
                return (
                  <tr
                    key={u.id}
                    className="border-b border-white/5 hover:bg-white/[0.03] transition-colors"
                  >
                    {/* User */}
                    <td className="px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 rounded-full bg-violet-600/20 flex items-center justify-center shrink-0">
                          <span className="text-[11px] font-700 text-violet-300">
                            {(u.full_name || u.email || '?')[0].toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-[13px] text-white font-500 leading-tight truncate max-w-[160px]">
                            {u.full_name || <span className="text-white/30 italic">Sem nome</span>}
                          </p>
                          <p className="text-[11px] text-white/30 truncate max-w-[160px]">{u.email}</p>
                        </div>
                      </div>
                    </td>

                    {/* Plan */}
                    <td className="px-4 py-3.5">
                      <span className="text-[12px] font-500 text-white/60 bg-white/5 px-2 py-0.5 rounded-md whitespace-nowrap">
                        {u.plan_name}
                      </span>
                    </td>

                    {/* Last access */}
                    <td className="px-4 py-3.5">
                      <p className="text-[12px] text-white/70 whitespace-nowrap">{fmtDate(u.last_access)}</p>
                      {u.days_since_access !== null && (
                        <p className="text-[11px] text-white/30 mt-0.5">
                          {u.days_since_access === 0 ? 'hoje' : `${u.days_since_access}d atrás`}
                        </p>
                      )}
                    </td>

                    {/* Sessions */}
                    <td className="px-4 py-3.5">
                      <div className="flex items-baseline gap-1">
                        <span className="text-[16px] font-700 text-white">{u.sessions_in_period}</span>
                        <span className="text-[11px] text-white/30">no período</span>
                      </div>
                      {u.total_sessions !== u.sessions_in_period && (
                        <p className="text-[11px] text-white/20">{u.total_sessions} total</p>
                      )}
                    </td>

                    {/* Device */}
                    <td className="px-4 py-3.5">
                      {u.device_type ? (
                        <div className="flex items-center gap-1.5">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className={`${deviceIcon} text-white/40 text-[14px]`} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-[12px] text-white/60 capitalize">{u.device_type}</p>
                            {(u.browser_name || u.os_name) && (
                              <p className="text-[11px] text-white/30 whitespace-nowrap">
                                {[u.browser_name, u.os_name].filter(Boolean).join(' · ')}
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <span className="text-white/20 text-[12px]">—</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3.5">
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-600 px-2.5 py-1 rounded-full whitespace-nowrap ${activity.cls}`}>
                        <i className={`${activity.icon} text-[11px]`} />
                        {activity.label}
                      </span>
                    </td>
                  </tr>
                );
              })}

              {paginated.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center py-14">
                    <i className="ri-user-search-line text-3xl text-white/20 mb-2 block" />
                    <p className="text-white/30 text-[13px]">Nenhum usuário encontrado com esses filtros</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-[12px] text-white/30">
            Mostrando {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} de {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-white/40 hover:text-white disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-left-s-line text-[16px]" />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageNum =
                totalPages <= 5 ? i + 1
                : page <= 3 ? i + 1
                : page >= totalPages - 2 ? totalPages - 4 + i
                : page - 2 + i;
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`w-8 h-8 flex items-center justify-center rounded-xl text-[13px] font-600 cursor-pointer transition-colors ${
                    page === pageNum
                      ? 'bg-violet-600 text-white'
                      : 'bg-white/5 border border-white/10 text-white/40 hover:text-white'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/5 border border-white/10 text-white/40 hover:text-white disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed transition-colors"
            >
              <i className="ri-arrow-right-s-line text-[16px]" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
